// Manage Tags sheet — modal pop-up from the todos page header.
// Lets the user add new tags, rename existing ones, delete (with
// affected-item count from the server), and reorder via up/down arrows.
//
// We keep the surface modest deliberately: no drag-to-reorder, no
// colors. The whole point is "extremely easy to manage with as little
// by hand as possible" — same north star as Pantry — and giving the
// wife a frictionless way to add a "yard" or "garage" tag was the
// actual ask.

import { useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import type { TodoTag } from "../../lib/rpc";
import type { UseTodoTags } from "../../lib/use-todo-tags";

interface ManageTagsProps {
  tags: UseTodoTags;
  notify: (msg: string) => void;
  onClose: () => void;
}

export function ManageTagsSheet({ tags, notify, onClose }: ManageTagsProps) {
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const onAdd = async () => {
    const name = draft.trim();
    if (!name) return;
    setDraft("");
    const t = await tags.create(name);
    if (t) notify(`Added #${t.name}`);
  };

  const beginEdit = (t: TodoTag) => {
    setEditingId(t.id);
    setEditValue(t.name);
  };
  const commitEdit = async () => {
    if (editingId === null) return;
    const v = editValue.trim();
    setEditingId(null);
    if (!v) return;
    const next = await tags.rename(editingId, v);
    if (next) notify(`Renamed to #${next.name}`);
  };

  const onDelete = async (t: TodoTag) => {
    const ok = await confirm({
      title: `Delete #${t.name}?`,
      message: `Existing todos with this tag will be untagged. This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const affected = await tags.remove(t.id);
    notify(affected > 0 ? `Deleted #${t.name} · untagged ${affected} todo${affected === 1 ? "" : "s"}` : `Deleted #${t.name}`);
  };

  const move = (idx: number, delta: number) => {
    const next = tags.tags.slice();
    const swap = idx + delta;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    void tags.reorder(next.map((t) => t.id));
  };

  return (
    <div className="confirm-bg" onClick={onClose}>
      <div className="manage-tags-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Manage tags">
        <header className="manage-tags-head">
          <h3>Tags</h3>
          <button className="icon-btn" onClick={onClose} type="button" aria-label="Close" title="Close">
            <Icon name="x" size={13} />
          </button>
        </header>

        <p className="manage-tags-sub">
          Tags label todos so you can scan by topic. Renames apply everywhere
          the tag is used; deleting strips it from existing todos.
        </p>

        <div className="manage-tags-list">
          {tags.tags.map((t, idx) => (
            <div className="manage-tags-row" key={t.id}>
              {editingId === t.id ? (
                <input
                  className="manage-tags-input"
                  value={editValue}
                  autoFocus
                  onInput={(e) => setEditValue((e.currentTarget as HTMLInputElement).value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                    if (e.key === "Escape") { setEditingId(null); }
                  }}
                />
              ) : (
                <button className="manage-tags-name" onClick={() => beginEdit(t)} type="button">
                  #{t.name}
                </button>
              )}
              <div className="manage-tags-actions">
                <button
                  className="manage-tags-icon"
                  type="button"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  aria-label="Move up"
                  title="Move up"
                >
                  <Icon name="arrow-up" size={12} />
                </button>
                <button
                  className="manage-tags-icon"
                  type="button"
                  disabled={idx === tags.tags.length - 1}
                  onClick={() => move(idx, 1)}
                  aria-label="Move down"
                  title="Move down"
                >
                  <Icon name="arrow-down" size={12} />
                </button>
                <button
                  className="manage-tags-icon manage-tags-icon-edit"
                  type="button"
                  onClick={() => beginEdit(t)}
                  aria-label="Rename"
                  title="Rename"
                >
                  <Icon name="edit" size={12} />
                </button>
                <button
                  className="manage-tags-icon manage-tags-icon-del"
                  type="button"
                  onClick={() => onDelete(t)}
                  aria-label="Delete"
                  title="Delete"
                >
                  <Icon name="x" size={12} />
                </button>
              </div>
            </div>
          ))}
          {tags.tags.length === 0 && !tags.loading && (
            <div className="manage-tags-empty">No tags yet. Add one below.</div>
          )}
        </div>

        <div className="manage-tags-add">
          <input
            placeholder="New tag name (e.g. garage)"
            value={draft}
            onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onAdd(); } }}
            maxLength={32}
          />
          <button className="manage-tags-add-btn" type="button" onClick={onAdd} disabled={!draft.trim()}>
            Add
          </button>
        </div>

        <footer className="manage-tags-foot">
          <button className="manage-tags-done" type="button" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
